// Risk Analytics Dashboard
// Phase 2 Week 7-8: Comprehensive risk analytics and visualization

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  AlertTriangle, 
  Activity, 
  BarChart3, 
  PieChart, 
  Target,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';
import { useProtocols } from '../../hooks/useProtocols';
import { useProtocolRisks } from '../../hooks/useRisks';
import { riskCalculationService } from '../../services/riskCalculationService';
import { riskService } from '../../services/riskService';
import './RiskAnalyticsDashboard.css';

const RiskAnalyticsDashboard = () => {
  const { t } = useTranslation('risks');
  const { protocols, loading: protocolsLoading } = useProtocols();
  const [allRisks, setAllRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredProtocols, setFilteredProtocols] = useState([]);
  const [filters, setFilters] = useState({
    minRPN: 0,
    maxRPN: 1000,
    criticality: 'all',
    department: 'all'
  });
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [trendData, setTrendData] = useState(null);

  // Load all risks for all protocols
  useEffect(() => {
    const loadAllRisks = async () => {
      try {
        setLoading(true);
        const risks = await riskService.getAllRisks();
        setAllRisks(risks);
        setFilteredProtocols(protocols || []);
      } catch (err) {
        console.error('Error loading risks:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (protocols && protocols.length > 0) {
      loadAllRisks();
    }
  }, [protocols]);

  // Calculate statistics
  const calculateStatistics = () => {
    if (!allRisks || allRisks.length === 0) {
      return {
        totalRisks: 0,
        totalProtocols: 0,
        avgRPN: 0,
        maxRPN: 0,
        criticalityDistribution: {
          Critical: 0,
          High: 0,
          Medium: 0,
          Low: 0
        }
      };
    }

    const rpnValues = allRisks.map(r => r.rpn || 0);
    const avgRPN = rpnValues.reduce((sum, rpn) => sum + rpn, 0) / rpnValues.length;
    const maxRPN = Math.max(...rpnValues);

    const criticalityDistribution = {
      Critical: allRisks.filter(r => r.criticality === 'Critical').length,
      High: allRisks.filter(r => r.criticality === 'High').length,
      Medium: allRisks.filter(r => r.criticality === 'Medium').length,
      Low: allRisks.filter(r => r.criticality === 'Low').length
    };

    // Get unique protocol IDs
    const uniqueProtocols = new Set(allRisks.map(r => r.protocolId).filter(Boolean));

    return {
      totalRisks: allRisks.length,
      totalProtocols: uniqueProtocols.size,
      avgRPN: Math.round(avgRPN * 100) / 100,
      maxRPN,
      criticalityDistribution
    };
  };

  // Get high-risk protocols
  const getHighRiskProtocols = () => {
    if (!protocols || !allRisks) return [];

    return protocols
      .map(protocol => {
        const protocolRisks = allRisks.filter(r => r.protocolId === protocol.id);
        if (protocolRisks.length === 0) return null;

        const rpnValues = protocolRisks.map(r => r.rpn || 0);
        const totalRPN = rpnValues.reduce((sum, rpn) => sum + rpn, 0);
        const avgRPN = totalRPN / protocolRisks.length;
        const maxRPN = Math.max(...rpnValues);

        return {
          ...protocol,
          riskCount: protocolRisks.length,
          totalRPN,
          avgRPN: Math.round(avgRPN * 100) / 100,
          maxRPN,
          criticalRisks: protocolRisks.filter(r => r.rpn >= 500).length
        };
      })
      .filter(p => p !== null)
      .sort((a, b) => b.maxRPN - a.maxRPN)
      .slice(0, 10);
  };

  // Get RPN distribution
  const getRPNDistribution = () => {
    const ranges = [
      { label: '1-49', min: 1, max: 49, color: '#28a745' },
      { label: '50-199', min: 50, max: 199, color: '#ffc107' },
      { label: '200-499', min: 200, max: 499, color: '#fd7e14' },
      { label: '500+', min: 500, max: 1000, color: '#dc3545' }
    ];

    return ranges.map(range => {
      const count = allRisks.filter(r => {
        const rpn = r.rpn || 0;
        return rpn >= range.min && rpn <= range.max;
      }).length;
      return { ...range, count };
    });
  };

  // Get biological entity exposure map
  const getBiologicalEntityMap = () => {
    const entityMap = new Map();

    allRisks.forEach(risk => {
      if (risk.biological_entities && Array.isArray(risk.biological_entities)) {
        risk.biological_entities.forEach(entity => {
          const entityId = entity.biologicalEntityId || entity.id;
          const entityName = entity.biologicalEntityName || entity.name || 'Unknown';

          if (!entityMap.has(entityId)) {
            entityMap.set(entityId, {
              id: entityId,
              name: entityName,
              riskCount: 0,
              totalRPN: 0,
              protocols: new Set()
            });
          }

          const entityData = entityMap.get(entityId);
          entityData.riskCount += 1;
          entityData.totalRPN += risk.rpn || 0;
          if (risk.protocolId) {
            entityData.protocols.add(risk.protocolId);
          }
        });
      }
    });

    return Array.from(entityMap.values()).map(entity => ({
      ...entity,
      avgRPN: Math.round((entity.totalRPN / entity.riskCount) * 100) / 100,
      protocolCount: entity.protocols.size
    })).sort((a, b) => b.riskCount - a.riskCount);
  };

  // Load trend data for selected protocol
  const loadTrendData = async (protocolId) => {
    try {
      const trend = await riskCalculationService.getRPNTrend(protocolId, 30);
      setTrendData(trend);
    } catch (err) {
      console.error('Error loading trend data:', err);
    }
  };

  const stats = calculateStatistics();
  const highRiskProtocols = getHighRiskProtocols();
  const rpnDistribution = getRPNDistribution();
  const entityMap = getBiologicalEntityMap();

  if (loading || protocolsLoading) {
    return (
      <div className="risk-analytics-dashboard">
        <div className="loading-state">
          <RefreshCw className="spinner" size={32} />
          <p>{t('loadingRiskAnalytics')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="risk-analytics-dashboard">
        <div className="error-state">
          <AlertTriangle size={32} />
          <p>{t('errorLoadingRiskAnalytics', { message: error.message })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="risk-analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>{t('riskAnalyticsTitle')}</h1>
          <p>{t('riskAnalyticsSubtitle')}</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary">
            <Download size={16} />
            {t('exportReport')}
          </button>
          <button className="btn btn-secondary">
            <Filter size={16} />
            {t('filters')}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalRisks}</div>
            <div className="stat-label">{t('totalRisksLabel')}</div>
          </div>
        </div>

        <div className="stat-card protocols">
          <div className="stat-icon">
            <Target size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalProtocols}</div>
            <div className="stat-label">{t('protocolsWithRisks')}</div>
          </div>
        </div>

        <div className="stat-card avg-rpn">
          <div className="stat-icon">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.avgRPN}</div>
            <div className="stat-label">{t('averageRpn')}</div>
          </div>
        </div>

        <div className="stat-card max-rpn">
          <div className="stat-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.maxRPN}</div>
            <div className="stat-label">{t('maximumRpn')}</div>
          </div>
        </div>
      </div>

      {/* Risk Distribution */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <PieChart size={20} />
            {t('riskDistribution')}
          </h2>
        </div>
        <div className="distribution-chart">
          {rpnDistribution.map(range => {
            const percentage = stats.totalRisks > 0 
              ? Math.round((range.count / stats.totalRisks) * 100) 
              : 0;
            return (
              <div key={range.label} className="distribution-bar">
                <div className="bar-info">
                  <span className="bar-label">{range.label}</span>
                  <span className="bar-count">{range.count} ({percentage}%)</span>
                </div>
                <div className="bar-container">
                  <div
                    className="bar-fill"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: range.color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Criticality Pyramid */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <BarChart3 size={20} />
            {t('criticalityDistribution')}
          </h2>
        </div>
        <div className="criticality-pyramid">
          <div className="pyramid-level critical" style={{ width: `${(stats.criticalityDistribution.Critical / stats.totalRisks) * 100 || 0}%` }}>
            <span className="level-label">{t('critical')} ({stats.criticalityDistribution.Critical})</span>
          </div>
          <div className="pyramid-level high" style={{ width: `${(stats.criticalityDistribution.High / stats.totalRisks) * 100 || 0}%` }}>
            <span className="level-label">{t('high')} ({stats.criticalityDistribution.High})</span>
          </div>
          <div className="pyramid-level medium" style={{ width: `${(stats.criticalityDistribution.Medium / stats.totalRisks) * 100 || 0}%` }}>
            <span className="level-label">{t('medium')} ({stats.criticalityDistribution.Medium})</span>
          </div>
          <div className="pyramid-level low" style={{ width: `${(stats.criticalityDistribution.Low / stats.totalRisks) * 100 || 0}%` }}>
            <span className="level-label">{t('low')} ({stats.criticalityDistribution.Low})</span>
          </div>
        </div>
      </div>

      {/* High-Risk Protocols */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <AlertTriangle size={20} />
            {t('highRiskProtocols')}
          </h2>
        </div>
        <div className="high-risk-table-container">
          <table className="high-risk-table">
            <thead>
              <tr>
                <th>{t('protocolName')}</th>
                <th>{t('riskCount')}</th>
                <th>{t('avgRpn')}</th>
                <th>{t('maxRpn')}</th>
                <th>{t('criticalRisks')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {highRiskProtocols.map(protocol => (
                <tr key={protocol.id}>
                  <td>
                    <strong>{protocol.name || protocol.protocol_name}</strong>
                    {protocol.protocol_code && (
                      <div className="protocol-code">{protocol.protocol_code}</div>
                    )}
                  </td>
                  <td>{protocol.riskCount}</td>
                  <td>
                    <span className="rpn-badge">{protocol.avgRPN}</span>
                  </td>
                  <td>
                    <span className={`rpn-badge ${protocol.maxRPN >= 500 ? 'critical' : protocol.maxRPN >= 200 ? 'high' : 'medium'}`}>
                      {protocol.maxRPN}
                    </span>
                  </td>
                  <td>
                    {protocol.criticalRisks > 0 && (
                      <span className="critical-badge">{protocol.criticalRisks}</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-link"
                      onClick={() => {
                        setSelectedProtocol(protocol);
                        loadTrendData(protocol.id);
                      }}
                    >
                      {t('viewDetails')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Biological Entity Exposure Map */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <Activity size={20} />
            {t('biologicalEntityExposureMap')}
          </h2>
        </div>
        <div className="entity-map">
          {entityMap.slice(0, 20).map(entity => (
            <div key={entity.id} className="entity-card">
              <div className="entity-name">{entity.name}</div>
              <div className="entity-stats">
                <span className="stat-item">
                  <strong>{entity.riskCount}</strong> {t('risksCount', { count: entity.riskCount })}
                </span>
                <span className="stat-item">
                  <strong>{entity.avgRPN}</strong> {t('avgRpnShort')}
                </span>
                <span className="stat-item">
                  <strong>{entity.protocolCount}</strong> {t('protocolsCount', { count: entity.protocolCount })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RiskAnalyticsDashboard;



